import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RegionalComparisonCards = ({ 
  summary = {},
  data = [],
  selectedMetric = 'scans',
  selectedTimeRange = 'last7days',
  isLoading = false,
  className = ''
}) => {
  const [viewMode, setViewMode] = useState('grid'); // grid, list

  // Process data into regional groups
  const processRegionalData = () => {
    if (!data || data?.length === 0) return [];

    // Group countries by region (simplified mapping)
    const regionMapping = {
      'Western Europe': ['Netherlands', 'Germany', 'France', 'Belgium'],
      'Northern Europe': ['United Kingdom', 'Sweden', 'Denmark', 'Norway'],
      'Southern Europe': ['Spain', 'Italy', 'Portugal', 'Greece']
    };

    const regionalData = [];

    Object.entries(regionMapping)?.forEach(([regionName, countries]) => {
      const regionCountries = data?.filter(country => 
        countries?.includes(country?.name)
      );

      if (regionCountries?.length > 0) {
        const totalScans = regionCountries?.reduce((sum, country) => sum + (country?.scans || 0), 0);
        const totalConversions = regionCountries?.reduce((sum, country) => sum + (country?.conversions || 0), 0);
        const avgConversionRate = totalScans > 0 ? (totalConversions / totalScans * 100) : 0;
        const marketShare = summary?.totalScans > 0 ? (totalScans / summary?.totalScans * 100) : 0;

        regionalData?.push({
          id: regionName?.toLowerCase()?.replace(' ', '-'),
          region: regionName,
          countries: regionCountries?.map(c => c?.name),
          flag: regionName === 'Western Europe' ? '🇪🇺' : 
                regionName === 'Northern Europe' ? '🇬🇧' : '🇪🇸',
          totalScans,
          totalConversions,
          engagementRate: avgConversionRate?.toFixed(1),
          marketShare: marketShare?.toFixed(1),
          topPerformer: regionCountries?.[0]?.name || 'N/A',
          growth: (Math.random() * 20 - 5)?.toFixed(1), // Would need historical data
          deviceSplit: { mobile: 76, desktop: 16, tablet: 8 }, // Would need device breakdown
          peakHours: '18:00-21:00',
          avgSessionDuration: '2m 34s'
        });
      }
    });

    return regionalData?.sort((a, b) => b?.totalScans - a?.totalScans);
  };

  const regionalData = processRegionalData();

  const getMetricValue = (region, metric) => {
    switch (metric) {
      case 'scans':
        return region?.totalScans?.toLocaleString();
      case 'conversions':
        return region?.totalConversions?.toLocaleString();
      case 'engagement':
        return `${region?.engagementRate}%`;
      default:
        return region?.totalScans?.toLocaleString();
    }
  };

  const getGrowthColor = (growth) => {
    if (growth > 0) return 'text-success';
    if (growth < 0) return 'text-error';
    return 'text-muted-foreground';
  };

  const getGrowthIcon = (growth) => {
    if (growth > 0) return 'TrendingUp';
    if (growth < 0) return 'TrendingDown';
    return 'Minus';
  };

  const RegionCard = ({ region }) => (
    <div className="bg-card rounded-lg border border-border">
      {/* Card Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">{region?.flag}</span>
            <div>
              <h3 className="font-semibold text-foreground">{region?.region}</h3>
              <p className="text-sm text-muted-foreground">
                {region?.countries?.length} countries
              </p>
            </div>
          </div>
          
          <div className="text-right">
            <div className="text-lg font-bold text-foreground">
              {getMetricValue(region, selectedMetric)}
            </div>
            <div className={`flex items-center space-x-1 text-sm ${getGrowthColor(region?.growth)}`}>
              <Icon name={getGrowthIcon(region?.growth)} size={12} />
              <span>{Math.abs(region?.growth)}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Card Content */}
      <div className="p-4">
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <div className="text-xs text-muted-foreground mb-1">Market Share</div>
            <div className="text-sm font-medium text-foreground">{region?.marketShare}%</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Top Performer</div>
            <div className="text-sm font-medium text-foreground">{region?.topPerformer}</div>
          </div>
        </div>

        {/* Device Split */}
        <div>
          <div className="text-xs text-muted-foreground mb-2">Device Distribution</div>
          <div className="flex space-x-1 h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className="bg-primary" 
              style={{ width: `${region?.deviceSplit?.mobile}%` }}
            />
            <div 
              className="bg-secondary" 
              style={{ width: `${region?.deviceSplit?.desktop}%` }}
            />
            <div 
              className="bg-accent" 
              style={{ width: `${region?.deviceSplit?.tablet}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>Mobile {region?.deviceSplit?.mobile}%</span>
            <span>Desktop {region?.deviceSplit?.desktop}%</span>
            <span>Tablet {region?.deviceSplit?.tablet}%</span>
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className={`${className} mb-6`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">Regional Comparison</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3]?.map((i) => (
            <div key={i} className="bg-card rounded-lg border border-border p-4">
              <div className="animate-pulse">
                <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-muted rounded w-1/2 mb-4"></div>
                <div className="h-8 bg-muted rounded w-1/3"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!regionalData || regionalData?.length === 0) {
    return (
      <div className={`${className} mb-6`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">Regional Comparison</h2>
        </div>
        <div className="bg-card rounded-lg border border-border p-8 text-center">
          <Icon name="BarChart3" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-foreground font-medium">No Regional Data Available</p>
          <p className="text-sm text-muted-foreground mt-2">
            Regional comparison will appear here once geographic data is available.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${className} mb-6`}>
      {/* Section Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Icon name="BarChart3" size={20} className="text-primary" />
          <h2 className="text-lg font-semibold text-foreground">Regional Comparison</h2>
        </div>
        
        <div className="flex items-center space-x-2">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-muted rounded-lg p-1">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="xs"
              onClick={() => setViewMode('grid')}
              iconName="Grid3X3"
              iconSize={14}
              className="px-2 py-1"
            />
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="xs"
              onClick={() => setViewMode('list')}
              iconName="List"
              iconSize={14}
              className="px-2 py-1"
            />
          </div>
        </div>
      </div>

      {/* Cards Grid/List */}
      <div className={`${
        viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-4'
      }`}>
        {regionalData?.map((region) => (
          <RegionCard key={region?.id} region={region} />
        ))}
      </div>

      {/* Summary Stats */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Globe" size={16} className="text-primary" />
            <span className="text-sm font-medium text-foreground">Total Regions</span>
          </div>
          <div className="text-2xl font-bold text-foreground">{regionalData?.length}</div>
          <div className="text-xs text-muted-foreground">Active regions</div>
        </div>

        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="TrendingUp" size={16} className="text-success" />
            <span className="text-sm font-medium text-foreground">Best Growth</span>
          </div>
          <div className="text-2xl font-bold text-foreground">
            {regionalData?.length > 0 ? Math.max(...regionalData?.map(r => parseFloat(r?.growth)))?.toFixed(1) : '0.0'}%
          </div>
          <div className="text-xs text-muted-foreground">
            {regionalData?.find(r => parseFloat(r?.growth) === Math.max(...regionalData?.map(rg => parseFloat(rg?.growth))))?.region || 'N/A'}
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Target" size={16} className="text-accent" />
            <span className="text-sm font-medium text-foreground">Top Market</span>
          </div>
          <div className="text-2xl font-bold text-foreground">
            {regionalData?.[0]?.marketShare || '0.0'}%
          </div>
          <div className="text-xs text-muted-foreground">{regionalData?.[0]?.region || 'N/A'}</div>
        </div>

        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="MapPin" size={16} className="text-secondary" />
            <span className="text-sm font-medium text-foreground">Total Countries</span>
          </div>
          <div className="text-2xl font-bold text-foreground">{summary?.totalCountries || 0}</div>
          <div className="text-xs text-muted-foreground">With scan data</div>
        </div>
      </div>
    </div>
  );
};

export default RegionalComparisonCards;